import {
  DocumentPdfPane,
  DocumentViewer,
  type DocumentPaneRenderProps,
  type DocumentViewerHighlight,
} from '@aecom-ai-engineering/react-components';
import { Button } from '@consigli/facade';
import { type DocumentContradictionChunkLink } from '@consigli/types';
import { type FC, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { HiOutlineXMark } from 'react-icons/hi2';

import { formatPageNumbers } from './contradiction-utils';
import { toChunkBox } from './document-source-utils';
import { HighlightedText } from './highlighted-text';
import { SOURCE_PANEL_CLASS, SOURCE_PANEL_SLIDE_MS } from './source-panel-slide';

export interface DocumentSourceChunk extends Pick<
  DocumentContradictionChunkLink,
  | 'blobId'
  | 'documentPackageId'
  | 'fileName'
  | 'pageNumbers'
  | 'highlightText'
  | 'chunkText'
  | 'pageIndex'
  | 'x0'
  | 'y0'
  | 'x1'
  | 'y1'
> {
  id: string;
}

interface DocumentSourceModalProps {
  chunk: DocumentSourceChunk | null;
  readUrl?: string;
  hasError?: boolean;
  isUnconvertible?: boolean;
  onClose: () => void;
}

const StatusPanel: FC<{ heading: string; onClose: () => void; children: ReactNode }> = ({
  heading,
  onClose,
  children,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={heading}
        className="flex w-full max-w-md flex-col gap-3 rounded-lg bg-white p-4 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4">
          <h2 className="truncate text-sm font-semibold text-day-neutral-dark" title={heading}>
            {heading}
          </h2>
          <Button
            tertiary
            iconOnly
            icon={HiOutlineXMark}
            iconSize={20}
            className="!p-1 shrink-0"
            onClick={onClose}
            aria-label={t('meeting-secretary.meetings.cancel', 'Close')}
          />
        </div>
        <p className="text-sm text-day-neutral-dark">{children}</p>
      </div>
    </div>,
    document.body,
  );
};

const SourceSidebar: FC<{ chunk: DocumentSourceChunk; pages?: string }> = ({ chunk, pages }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4 p-4 text-sm">
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-wider text-day-neutral-subtle">
          {t('meeting-secretary.contradictions.document-source-title')}
        </span>
        {chunk.fileName && (
          <span className="font-semibold text-day-neutral-dark break-words">{chunk.fileName}</span>
        )}
        {pages && <span className="text-xs text-day-neutral-subtle">{pages}</span>}
      </div>

      {chunk.chunkText && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wider text-day-neutral-subtle">
            {t('meeting-secretary.contradictions.document-text')}
          </span>
          <span className="whitespace-pre-line text-sm italic text-day-neutral-dark">
            <HighlightedText text={chunk.chunkText} highlight={chunk.highlightText} />
          </span>
        </div>
      )}
    </div>
  );
};

const SourceDrawer: FC<{
  chunk: DocumentSourceChunk;
  readUrl: string;
  heading: string;
  onClose: () => void;
}> = ({ chunk, readUrl, heading, onClose }) => {
  const [open, setOpen] = useState(true);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const startClose = useCallback(() => {
    setOpen(false);
    closeTimer.current = setTimeout(onClose, SOURCE_PANEL_SLIDE_MS);
  }, [onClose]);

  const box = toChunkBox(chunk);
  const pages = box ? `Page ${box.pageIndex + 1}` : formatPageNumbers(chunk.pageNumbers);

  const firstPage = chunk.pageNumbers?.[0];
  const initialPage = box ? box.pageIndex + 1 : firstPage && firstPage > 0 ? firstPage : 1;

  const highlights = useMemo((): DocumentViewerHighlight[] | undefined => {
    if (!box) return undefined;
    return [{ x0: box.x0, y0: box.y0, x1: box.x1, y1: box.y1, pageNumber: box.pageIndex + 1 }];
  }, [box]);

  const renderers = useMemo(
    () => ({
      pdf: (props: DocumentPaneRenderProps) => (
        <DocumentPdfPane
          active={props.active}
          caption={props.caption}
          onSidebarContextChange={props.onSidebarContextChange}
          src={props.src}
          initialPage={initialPage}
          highlights={highlights}
          continuousScroll
          searchable
        />
      ),
    }),
    [initialPage, highlights],
  );

  return (
    <DocumentViewer
      open={open}
      onOpenChange={(details) => {
        if (!details.open) startClose();
      }}
      className={SOURCE_PANEL_CLASS}
      src={readUrl}
      title={heading}
      primaryTitle={heading}
      showSidebar
      showNavigationActions={false}
      renderers={renderers}
    >
      <SourceSidebar chunk={chunk} pages={pages} />
    </DocumentViewer>
  );
};

export const DocumentSourceModal: FC<DocumentSourceModalProps> = ({
  chunk,
  readUrl,
  hasError = false,
  isUnconvertible = false,
  onClose,
}) => {
  const { t } = useTranslation();

  if (!chunk) return null;

  const heading =
    chunk.fileName?.trim() || t('meeting-secretary.contradictions.document-source-title');

  if (!chunk.blobId) {
    return (
      <StatusPanel heading={heading} onClose={onClose}>
        {t('meeting-secretary.contradictions.no-source-document')}
      </StatusPanel>
    );
  }

  if (isUnconvertible) {
    return (
      <StatusPanel heading={heading} onClose={onClose}>
        {t('meeting-secretary.contradictions.source-not-previewable')}
      </StatusPanel>
    );
  }

  if (hasError) {
    return (
      <StatusPanel heading={heading} onClose={onClose}>
        {t('meeting-secretary.contradictions.error-loading-document-source')}
      </StatusPanel>
    );
  }

  if (!readUrl) return null;

  return (
    <SourceDrawer
      key={chunk.id}
      chunk={chunk}
      readUrl={readUrl}
      heading={heading}
      onClose={onClose}
    />
  );
};
